import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiHome, FiGrid, FiBriefcase, FiUsers, FiPackage,
  FiMail, FiFileText, FiChevronLeft, FiChevronRight,
  FiUserCheck, FiActivity, FiUserPlus, FiSettings, FiVideo, FiCopy, FiLayers, FiHelpCircle,
} from 'react-icons/fi';
import { useCurrentAdmin } from '../../hooks/useCurrentAdmin';
import { useViewTransitionNavigation } from '../../hooks/useViewTransitionNavigation';
import Tooltip from '../common/Tooltip';
import type { AdminRole } from '../../types/api';

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  roles: AdminRole[];
}

const NAV_ITEMS: NavItem[] = [
  // Operativo — visible para varios roles según contexto
  { icon: FiHome, label: 'Inicio', path: '/home', roles: ['root', 'admin', 'supervisor', 'backseller', 'telemarket', 'viewer'] },
  // Vista global de instituciones (CRM + classroom + v1): solo administradores.
  { icon: FiLayers, label: 'Control', path: '/schools/control', roles: ['root', 'admin'] },
  // Telemarket NO ve "Instituciones" (base completa) — solo "Mis Instituciones".
  { icon: FiGrid, label: 'Instituciones', path: '/schools', roles: ['root', 'admin', 'supervisor', 'backseller', 'viewer'] },
  { icon: FiBriefcase, label: 'Mis Instituciones', path: '/my-schools', roles: ['backseller', 'telemarket'] },
  { icon: FiUserCheck, label: 'Mi equipo', path: '/team', roles: ['supervisor'] },
  { icon: FiUsers, label: 'Docentes', path: '/teachers', roles: ['root', 'admin', 'supervisor', 'backseller', 'telemarket', 'viewer'] },
  { icon: FiMail, label: 'Email', path: '/emails', roles: ['root', 'admin', 'supervisor', 'backseller', 'telemarket', 'viewer'] },
  { icon: FiFileText, label: 'Evaluaciones', path: '/evaluations', roles: ['root', 'admin', 'backseller'] },

  // Administración del sistema — solo admin/root, expuestos al nivel del menú principal
  { icon: FiCopy, label: 'Duplicados', path: '/schools/duplicates', roles: ['root', 'admin'] },
  { icon: FiPackage, label: 'Productos', path: '/products', roles: ['root', 'admin'] },
  { icon: FiUserPlus, label: 'Usuarios', path: '/users', roles: ['root', 'admin'] },
  { icon: FiActivity, label: 'Actividad', path: '/activity', roles: ['root', 'admin'] },
  { icon: FiVideo, label: 'Inscriptos webinar', path: '/webinar-contacts', roles: ['root', 'admin', 'supervisor'] },
  { icon: FiSettings, label: 'Configuración', path: '/settings', roles: ['root', 'admin'] },
  { icon: FiHelpCircle, label: 'Ayuda', path: '/help', roles: ['root', 'admin', 'supervisor', 'backseller', 'telemarket', 'viewer'] },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Llamado tras navegar. En móvil (offcanvas) lo usamos para cerrar el menú. */
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, onNavigate }) => {
  const { role } = useCurrentAdmin();
  const location = useLocation();
  const navigate = useViewTransitionNavigation();

  const visibleItems = NAV_ITEMS.filter((item) => role && item.roles.includes(role));

  const handleNavigate = (item: NavItem) => {
    navigate(item.path);
    onNavigate?.();
  };

  // The longest matching path wins, so `/schools/duplicates` lights its own
  // item and not "Instituciones" as well.
  const activePath = visibleItems
    .filter((item) => location.pathname.startsWith(item.path))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  const isActive = (path: string) => {
    if (path === '/home') return location.pathname === '/home';
    return path === activePath;
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-surface border-r border-border z-40 flex flex-col transition-[width] duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      <div className={`flex items-center h-14 border-b border-border px-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && <span className="text-sm font-bold text-content-primary truncate">Estrada CRM</span>}
        <button
          onClick={onToggle}
          className="p-1.5 rounded-lg text-content-secondary hover:bg-surface-tertiary transition-colors"
          title={collapsed ? 'Expandir' : 'Colapsar'}
        >
          {collapsed ? <FiChevronRight className="w-4 h-4" /> : <FiChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Tooltip key={item.path} content={item.label} placement="right" disabled={!collapsed}>
              <button
                onClick={() => handleNavigate(item)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-content-secondary hover:bg-surface-tertiary hover:text-content-primary'
                } ${collapsed ? 'justify-center mx-1.5' : ''}`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-primary-600' : ''}`} />
                {!collapsed && <span className="truncate flex-1 text-left">{item.label}</span>}
              </button>
            </Tooltip>
          );
        })}
      </nav>
    </aside>
  );
};

export default Sidebar;
